import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import ConfirmationDialog from '../../../components/ui/ConfirmationDialog';
import { supabase } from '../../../lib/supabase';

const ProfileModal = ({ person, roleType, onClose, onSave, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: person?.name,
    email: person?.email,
    phone: person?.phone,
    role: person?.role,
    status: person?.status,
    location: person?.location || '',
    emergencyContact: person?.emergencyContact || '',
    emergencyPhone: person?.emergencyPhone || '',
    skills: person?.skills ? [...person?.skills] : [],
    equipment: person?.equipment ? [...person?.equipment] : []
  });
  const [newSkill, setNewSkill] = useState('');
  const [newEquipment, setNewEquipment] = useState('');

  const statusOptions = [
    { value: 'Available', label: 'Available' },
    { value: 'On Job', label: 'On Job' },
    { value: 'Off Duty', label: 'Off Duty' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaveLoading(true);

    try {
      // Map status to database format
      const statusMap = {
        'Available': 'available',
        'On Job': 'on_job',
        'Off Duty': 'off_duty',
        'On Leave': 'on_leave'
      };

      // Update personnel record (don't update role or custom_role_name in edit mode)
      const { error: updateError } = await supabase
        ?.from('personnel')
        ?.update({
          name: formData?.name,
          email: formData?.email,
          phone: formData?.phone,
          status: statusMap?.[formData?.status] || 'available',
          location: formData?.location || null,
          emergency_contact: formData?.emergencyContact || null,
          emergency_phone: formData?.emergencyPhone || null,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', person?.id);

      if (updateError) {
        console.error('Error updating personnel:', updateError?.message);
        alert('Failed to update personnel. Please try again.');
        setSaveLoading(false);
        return;
      }

      // Update skills and equipment if technician
      if (roleType === 'technician') {
        await supabase?.from('personnel_skills')?.delete()?.eq('personnel_id', person?.id);
        if (formData?.skills?.length > 0) {
          const skillRows = formData?.skills?.map(skill => ({ personnel_id: person?.id, skill_name: skill }));
          await supabase?.from('personnel_skills')?.insert(skillRows);
        }
        await supabase?.from('personnel_equipment')?.delete()?.eq('personnel_id', person?.id);
        if (formData?.equipment?.length > 0) {
          const equipRows = formData?.equipment?.map(item => ({ personnel_id: person?.id, equipment_name: item }));
          await supabase?.from('personnel_equipment')?.insert(equipRows);
        }
      }

      // Call parent callback to refresh data
      const updatedPerson = { ...person, ...formData };
      onSave(updatedPerson);
      sendPersonnelNotification('personnel_edited', updatedPerson);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update personnel:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: person?.name,
      email: person?.email,
      phone: person?.phone,
      role: person?.role,
      status: person?.status,
      location: person?.location || '',
      emergencyContact: person?.emergencyContact || '',
      emergencyPhone: person?.emergencyPhone || '',
      skills: person?.skills ? [...person?.skills] : [],
      equipment: person?.equipment ? [...person?.equipment] : []
    });
    setNewSkill('');
    setNewEquipment('');
    setIsEditing(false);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);

    try {
      // Delete related records first (skills and equipment)
      await supabase?.from('personnel_skills')?.delete()?.eq('personnel_id', person?.id);
      await supabase?.from('personnel_equipment')?.delete()?.eq('personnel_id', person?.id);

      // Delete personnel record
      const { error: deleteError } = await supabase
        ?.from('personnel')
        ?.delete()
        ?.eq('id', person?.id);

      if (deleteError) {
        console.error('Error deleting personnel:', deleteError?.message);
        alert('Failed to delete personnel. Please try again.');
        setDeleteLoading(false);
        return;
      }

      // Call parent callback to refresh data
      if (onDelete) {
        onDelete(person?.id);
        sendPersonnelNotification('personnel_deleted', person);
      }
      setShowDeleteConfirm(false);
      onClose();
    } catch (error) {
      console.error('Failed to delete personnel:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const sendPersonnelNotification = async (type, personnelData) => {
    try {
      setEmailSending(true);
      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-personnel-notification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            type: type,
            recipientEmail: 'admin@rigjockey.com',
            recipientName: 'Admin',
            personnelName: personnelData?.name,
            personnelEmail: personnelData?.email,
            role: personnelData?.role,
            roleType: roleType,
            changedBy: 'System Admin'
          })
        }
      );

      const result = await response?.json();
      if (result?.success) {
        console.log('Personnel notification sent successfully');
      } else {
        console.error('Failed to send personnel notification:', result?.error);
      }
    } catch (error) {
      console.error('Error sending personnel notification:', error);
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1400] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between z-10">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
            Personnel Profile
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={24} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden flex-shrink-0 bg-muted">
              <Image
                src={person?.avatar}
                alt={person?.avatarAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
                {person?.name}
              </h3>
              <p className="text-muted-foreground mb-2">{person?.role}</p>
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                person?.status === 'Available' ? 'bg-success/10 text-success' :
                person?.status === 'On Job'? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'
              }`}>
                {person?.status}
              </span>
            </div>
          </div>

          <div className="border-t border-border pt-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-heading font-semibold text-foreground">
                Contact Information
              </h4>
              {!isEditing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  iconName="Edit"
                  iconPosition="left"
                >
                  Edit
                </Button>
              )}
            </div>

            {isEditing ? (
              <div className="space-y-4">
                <Input
                  label="Full Name"
                  type="text"
                  value={formData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  required
                />
                <Input
                  label="Email Address"
                  type="email"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  required
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  required
                />
                <Select
                  label="Status"
                  options={statusOptions}
                  value={formData?.status}
                  onChange={(value) => handleInputChange('status', value)}
                />
                {roleType === 'technician' && (
                  <Input
                    label="Current Location"
                    type="text"
                    value={formData?.location}
                    onChange={(e) => handleInputChange('location', e?.target?.value)}
                  />
                )}
                <Input
                  label="Emergency Contact Name"
                  type="text"
                  value={formData?.emergencyContact}
                  onChange={(e) => handleInputChange('emergencyContact', e?.target?.value)}
                />
                <Input
                  label="Emergency Contact Phone"
                  type="tel"
                  value={formData?.emergencyPhone}
                  onChange={(e) => handleInputChange('emergencyPhone', e?.target?.value)}
                />

                {roleType === 'technician' && (
                  <>
                    {/* Skills & Certifications */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Skills &amp; Certifications</label>
                      <div className="flex flex-wrap gap-2 mb-2 min-h-[32px]">
                        {formData?.skills?.map((skill, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary text-sm rounded-lg font-medium"
                          >
                            {skill}
                            <button
                              type="button"
                              onClick={() => handleInputChange('skills', formData?.skills?.filter((_, i) => i !== idx))}
                              className="ml-1 hover:text-red-500 transition-colors leading-none"
                            >
                              <Icon name="X" size={12} />
                            </button>
                          </span>
                        ))}
                        {formData?.skills?.length === 0 && (
                          <span className="text-sm text-muted-foreground italic">No skills added yet</span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newSkill}
                          onChange={(e) => setNewSkill(e?.target?.value)}
                          onKeyDown={(e) => {
                            if (e?.key === 'Enter' && newSkill?.trim()) {
                              e?.preventDefault();
                              handleInputChange('skills', [...(formData?.skills || []), newSkill?.trim()]);
                              setNewSkill('');
                            }
                          }}
                          placeholder="Type a skill and press Enter or +"
                          className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (newSkill?.trim()) {
                              handleInputChange('skills', [...(formData?.skills || []), newSkill?.trim()]);
                              setNewSkill('');
                            }
                          }}
                          iconName="Plus"
                        />
                      </div>
                    </div>

                    {/* Assigned Equipment */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Assigned Equipment</label>
                      <div className="space-y-2 mb-2">
                        {formData?.equipment?.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                            <span className="text-sm text-foreground">{item}</span>
                            <button
                              type="button"
                              onClick={() => handleInputChange('equipment', formData?.equipment?.filter((_, i) => i !== idx))}
                              className="p-1 hover:text-red-500 transition-colors"
                            >
                              <Icon name="Trash2" size={14} />
                            </button>
                          </div>
                        ))}
                        {formData?.equipment?.length === 0 && (
                          <p className="text-sm text-muted-foreground italic">No equipment assigned yet</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newEquipment}
                          onChange={(e) => setNewEquipment(e?.target?.value)}
                          onKeyDown={(e) => {
                            if (e?.key === 'Enter' && newEquipment?.trim()) {
                              e?.preventDefault();
                              handleInputChange('equipment', [...(formData?.equipment || []), newEquipment?.trim()]);
                              setNewEquipment('');
                            }
                          }}
                          placeholder="Type equipment name and press Enter or +"
                          className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (newEquipment?.trim()) {
                              handleInputChange('equipment', [...(formData?.equipment || []), newEquipment?.trim()]);
                              setNewEquipment('');
                            }
                          }}
                          iconName="Plus"
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="default"
                    fullWidth
                    onClick={handleSave}
                    loading={saveLoading}
                    disabled={saveLoading}
                    iconName="Save"
                    iconPosition="left"
                  >
                    Save Changes
                  </Button>
                  <Button variant="outline" onClick={handleCancel} fullWidth>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Icon name="Mail" size={20} color="var(--color-muted-foreground)" className="mt-1" />
                  <div>
                    <p className="text-sm font-caption text-muted-foreground mb-1">Email</p>
                    <p className="text-foreground">{person?.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Icon name="Phone" size={20} color="var(--color-muted-foreground)" className="mt-1" />
                  <div>
                    <p className="text-sm font-caption text-muted-foreground mb-1">Phone</p>
                    <p className="text-foreground">{person?.phone}</p>
                  </div>
                </div>
                {person?.location && (
                  <div className="flex items-start gap-3">
                    <Icon name="MapPin" size={20} color="var(--color-muted-foreground)" className="mt-1" />
                    <div>
                      <p className="text-sm font-caption text-muted-foreground mb-1">Location</p>
                      <p className="text-foreground">{person?.location}</p>
                    </div>
                  </div>
                )}
                {person?.emergencyContact && (
                  <div className="flex items-start gap-3">
                    <Icon name="AlertCircle" size={20} color="var(--color-muted-foreground)" className="mt-1" />
                    <div>
                      <p className="text-sm font-caption text-muted-foreground mb-1">Emergency Contact</p>
                      <p className="text-foreground">{person?.emergencyContact}</p>
                      <p className="text-muted-foreground text-sm">{person?.emergencyPhone}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {roleType === 'technician' && person?.skills && (
            <div className="border-t border-border pt-6">
              <h4 className="text-lg font-heading font-semibold text-foreground mb-4">
                Skills & Certifications
              </h4>
              <div className="flex flex-wrap gap-2">
                {person?.skills?.map((skill, index) => (
                  <span
                    key={index}
                    className="px-3 py-2 bg-primary/10 text-primary text-sm rounded-lg font-medium"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {roleType === 'technician' && person?.equipment && (
            <div className="border-t border-border pt-6">
              <h4 className="text-lg font-heading font-semibold text-foreground mb-4">
                Assigned Equipment
              </h4>
              <div className="space-y-2">
                {person?.equipment?.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <span className="text-foreground">{item}</span>
                    <Icon name="CheckCircle" size={18} color="var(--color-success)" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {person?.performanceMetrics && (
            <div className="border-t border-border pt-6">
              <h4 className="text-lg font-heading font-semibold text-foreground mb-4">
                Performance Metrics
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm font-caption text-muted-foreground mb-1">Jobs Completed</p>
                  <p className="text-2xl font-heading font-semibold text-foreground">
                    {person?.performanceMetrics?.jobsCompleted}
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm font-caption text-muted-foreground mb-1">Average Rating</p>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-heading font-semibold text-foreground">
                      {person?.performanceMetrics?.rating}
                    </p>
                    <Icon name="Star" size={20} color="var(--color-accent)" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-4 md:p-6 flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            fullWidth
          >
            Close
          </Button>
          {!isEditing && onDelete && (
            <Button
              variant="destructive"
              onClick={handleDeleteClick}
              fullWidth
              iconName="Trash2"
              iconPosition="left"
            >
              Delete Personnel
            </Button>
          )}
        </div>
      </div>
      </div>
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title="Delete Personnel"
        message={`Are you sure you want to delete ${person?.name}? This action cannot be undone and will remove all associated data.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        loading={deleteLoading}
        icon="Trash2"
      />
    </>
  );
};

export default ProfileModal;